import React, { useCallback, useEffect, useState } from "react";

import { drawolaT, useDrawolaT } from "../../storage/i18n";

import type { CloudFileMeta } from "../types";

function formatRelative(
  modifiedTime: number,
  t: (key: string, r?: { [k: string]: string | number }) => string,
): string {
  const diff = Date.now() - modifiedTime;
  const mins = Math.round(diff / 60_000);
  if (mins < 1) {
    return t("cloud.justNow");
  }
  if (mins < 60) {
    return t("cloud.minutesAgo", { count: mins });
  }
  const hrs = Math.round(mins / 60);
  if (hrs < 24) {
    return t("cloud.hoursAgo", { count: hrs });
  }
  const days = Math.round(hrs / 24);
  return t("cloud.daysAgo", { count: days });
}

type Props = {
  open: boolean;
  listFiles: () => Promise<CloudFileMeta[]>;
  onOpen: (fileId: string) => Promise<void>;
  onRename: (fileId: string, newName: string) => Promise<void>;
  onDelete: (fileId: string) => Promise<void>;
  onClose: () => void;
};

export const CloudFilePickerModal: React.FC<Props> = ({
  open,
  listFiles,
  onOpen,
  onRename,
  onDelete,
  onClose,
}) => {
  const t = useDrawolaT();
  const [files, setFiles] = useState<CloudFileMeta[] | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setFiles(null);
    const list = await listFiles();
    setFiles(list);
  }, [listFiles]);

  useEffect(() => {
    if (!open) {
      return;
    }
    refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const handleOpen = async (fileId: string) => {
    setBusy(true);
    try {
      await onOpen(fileId);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (meta: CloudFileMeta) => {
    const next = window.prompt(drawolaT("cloud.renamePrompt"), meta.name);
    if (!next || !next.trim() || next === meta.name) {
      return;
    }
    await onRename(meta.providerFileId, next.trim());
    refresh();
  };

  const handleDelete = async (meta: CloudFileMeta) => {
    if (
      !window.confirm(drawolaT("cloud.deleteConfirm", { name: meta.name }))
    ) {
      return;
    }
    await onDelete(meta.providerFileId);
    refresh();
  };

  return (
    <div
      className="drawOla-cloud-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="drawOla-cloud-modal__panel">
        <div className="drawOla-cloud-modal__header">
          <h3 className="drawOla-cloud-modal__title">
            {t("cloud.pickerTitle")}
          </h3>
          <button
            type="button"
            className="drawOla-cloud-modal__close"
            aria-label={t("cloud.cancel")}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="drawOla-cloud-modal__body">
          {files === null ? (
            <div className="drawOla-cloud-modal__empty">
              {t("cloud.loadingList")}
            </div>
          ) : files.length === 0 ? (
            <div className="drawOla-cloud-modal__empty">
              {t("cloud.emptyList")}
            </div>
          ) : (
            <ul className="drawOla-cloud-file-list">
              {files.map((f) => (
                <li
                  key={f.providerFileId}
                  className="drawOla-cloud-file-list__item"
                  onClick={() => !busy && handleOpen(f.providerFileId)}
                >
                  <span
                    className="drawOla-cloud-file-list__name"
                    title={f.name}
                  >
                    {f.name}
                  </span>
                  <span className="drawOla-cloud-file-list__time">
                    {formatRelative(f.modifiedTime, t)}
                  </span>
                  <span className="drawOla-cloud-file-list__actions">
                    <button
                      type="button"
                      className="drawOla-cloud-file-list__action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRename(f);
                      }}
                      title={t("cloud.renameTitle")}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="drawOla-cloud-file-list__action drawOla-cloud-file-list__action--danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(f);
                      }}
                      title={t("cloud.deleteTitle")}
                    >
                      ✕
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
